import { useState } from 'react';

export default function Services() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const services = [
    {
      title: 'Workflow Architecture',
      description: 'Design and mapping of operational workflows that eliminate repetitive manual processes.',
      icon: 'ri-flow-chart'
    },
    {
      title: 'Intelligent Automation',
      description: 'Automated systems for reporting, communication, task routing, and data processing.',
      icon: 'ri-robot-line'
    },
    {
      title: 'System Integration',
      description: 'Integration of CRM platforms, communication tools, marketing systems, and operational software.',
      icon: 'ri-links-line'
    },
    {
      title: 'Operational Intelligence',
      description: 'Real-time dashboards and reporting systems that deliver accurate business insights.',
      icon: 'ri-dashboard-line'
    },
    {
      title: 'Automation Monitoring',
      description: 'Continuous monitoring of automation workflows to ensure stability and reliability.',
      icon: 'ri-eye-line'
    },
    {
      title: 'Process Optimization',
      description: 'Ongoing improvements that increase efficiency and reduce operational complexity.',
      icon: 'ri-speed-up-line'
    }
  ];

  return (
    <section className="relative bg-[#1A1C1E] py-32 overflow-hidden">
      {/* Geometric architecture lines */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1000 1000" preserveAspectRatio="none">
          <defs>
            <pattern id="architectureGrid" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#00E5FF" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="1000" height="1000" fill="url(#architectureGrid)" />
          <line x1="0" y1="200" x2="1000" y2="200" stroke="#00E5FF" strokeWidth="1" opacity="0.3" />
          <line x1="0" y1="500" x2="1000" y2="500" stroke="#00E5FF" strokeWidth="1" opacity="0.3" />
          <line x1="0" y1="800" x2="1000" y2="800" stroke="#00E5FF" strokeWidth="1" opacity="0.3" />
          <line x1="200" y1="0" x2="200" y2="1000" stroke="#00E5FF" strokeWidth="1" opacity="0.3" />
          <line x1="500" y1="0" x2="500" y2="1000" stroke="#00E5FF" strokeWidth="1" opacity="0.3" />
          <line x1="800" y1="0" x2="800" y2="1000" stroke="#00E5FF" strokeWidth="1" opacity="0.3" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#F4F7F6] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Systems Built for Operational Clarity
          </h2>
          <p className="text-xl text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            Graystone AI designs automation infrastructure that eliminates repetitive operational work and restores clarity to growing businesses.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              className="relative group cursor-pointer"
            >
              <div 
                className="relative bg-[#1A1C1E] border border-[#7F8C8D]/20 rounded-lg p-8 h-full transition-all duration-300"
                style={{
                  borderColor: hoveredCard === index ? '#00E5FF' : 'rgba(127, 140, 141, 0.2)',
                  boxShadow: hoveredCard === index ? '0 0 30px rgba(0, 229, 255, 0.2)' : 'none'
                }}
              >
                {/* Icon */}
                <div className="w-16 h-16 flex items-center justify-center mb-6 relative">
                  <div 
                    className="absolute inset-0 bg-[#00E5FF]/10 rounded-lg transition-all duration-300"
                    style={{
                      opacity: hoveredCard === index ? 1 : 0.5,
                      transform: hoveredCard === index ? 'scale(1.1)' : 'scale(1)'
                    }}
                  ></div>
                  <i 
                    className={`${service.icon} text-4xl relative z-10 transition-colors duration-300`}
                    style={{
                      color: hoveredCard === index ? '#00E5FF' : '#7F8C8D'
                    }}
                  ></i>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-[#F4F7F6] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {service.description}
                </p>

                {/* Hover accent line */}
                <div 
                  className="absolute bottom-0 left-0 h-1 bg-[#00E5FF] transition-all duration-300 rounded-b-lg"
                  style={{
                    width: hoveredCard === index ? '100%' : '0%'
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}